<template>
  <iStyledCard v-if="editMode && hasNoFeedSelection" width="fill">
    <template #title>
      <iSubHeading align="center" font-size="large" variant="subtle">
        Please select a feed source
      </iSubHeading>
    </template>
  </iStyledCard>
  <iStyledCard v-else-if="editMode && isMissingStoryTypeCard" width="fill">
    <template #title>
      <iSubHeading align="center" font-size="large" variant="subtle">
        {{ getOptionValue(EmptyStateText) }}
      </iSubHeading>
    </template>
  </iStyledCard>
  <iRow
    v-else-if="!hasNoFeedSelection"
    :max-height="getOptionValue(MaxHeight)"
    :max-width="getOptionValue(MaxWidth)"
    :min-height="getOptionValue(MinHeight)"
    :min-width="getOptionValue(MinWidth)"
    :width="resolvedLayoutWidth"
    height="hug"
    overflow="auto"
  >
    <!-- Swallow editor-preview card clicks so they don't navigate (mirrors
         StudioFeedRowItem); covers SmartFeed's default entity-card renderer. -->
    <div class="feed-editor-click-guard" @click.capture="handleEditorPreviewClick">
      <SmartFeed
        :key="smartFeedKey"
        :default-entity-size="smartFeedEntityLayout"
        :default-feed-layout="smartFeedLayout"
        :edit-mode="shouldShowPlaceholders"
        :show-filters="filtersToExpose.length > 0"
        :filters-to-expose="filtersToExpose"
        :source="piniaStore"
        :load-more-behavior="smartFeedLoadMoreBehavior"
        :page-size-limit="smartFeedPageSizeLimit"
        :pagination-option="smartFeedPaginationOption"
        :load-more-text="getOptionValue(TextOption) || getOptionValue(SmartFeedSeeMoreText)"
        :load-more-button-variant="getOptionValue(ButtonType)"
        :load-more-button-size="getOptionValue(ButtonSize)"
        :load-more-button-style-overrides="buttonStyleOverrides"
        :load-more-button-text-overrides="textOnlyAttributes"
        :load-more-button-align="getOptionValue(SmartFeedSeeMoreAlignment)"
        :load-more-button-icon="getOptionValue(Icon)"
        :load-more-button-icon-position="getOptionValue(IconPosition)"
        :load-more-button-icon-color="getOptionValue(FontColor)"
        :gap="getOptionValue(LayoutGap)"
        :column-min-width="getOptionValue(SmartFeedColumnMinWidth)"
        :column-max-width="getOptionValue(SmartFeedColumnMaxWidth)"
        :filter-row-alignment="filterRowAlignment"
        :filter-row-style-overrides="filterRowStyleOverrides"
        :filter-pill-style-overrides="filterPillStyleOverrides"
        :filter-label-style-overrides="filterLabelStyleOverrides"
        :filter-icon-style-overrides="filterIconStyleOverrides"
        :filter-icon="filterIcon"
        :filter-label="filterLabel"
        :filter-label-position="filterLabelPosition"
        :content-alignment="inheritedContentAlignment"
        padding="none"
      >
        <!-- Render each row via StudioFeedRowItem only when a row-template DC is
           picked; otherwise the slot is absent and SmartFeed uses its own renderer. -->
        <template v-if="rowTemplateComponentId > 0" #item="{feedItem}">
          <StudioFeedRowItem
            :entity-id="feedItem.id"
            :dynamic-component-id="rowTemplateComponentId"
            :context-name="rowTemplateContextName"
            :theme-mode="themeMode"
            :edit-mode="editMode"
          />
        </template>
        <template v-if="showSeeAllOption" #above-content>
          <iRow width="fill" align="right">
            <iLink :href="seeAllHref">
              {{ getOptionValue(SmartFeedSeeAllText) }}
            </iLink>
          </iRow>
        </template>
        <template #empty-state>
          <!-- A picked empty-state component replaces the text card entirely;
               with none picked (id 0) this falls through to the card, so
               existing feeds render exactly as before. -->
          <StudioFeedRowItem
            v-if="emptyStateComponentId > 0"
            :entity-id="0"
            :dynamic-component-id="emptyStateComponentId"
            :theme-mode="themeMode"
            :edit-mode="editMode"
            size-to-card
            data-test="feed-empty-state-component"
          />
          <iStyledCard v-else width="fill">
            <template #title>
              <iSubHeading align="center" font-size="large" variant="subtle">
                {{ getOptionValue(EmptyStateText) }}
              </iSubHeading>
            </template>
          </iStyledCard>
        </template>
      </SmartFeed>
    </div>
  </iRow>
</template>

<script>
import SmartFeed from "SmartFeed/components/SmartFeedV2.vue";
import { buildExposedFilterStoreEntries } from "SmartFeed/helpers/smart-feed-filter-display";
import StudioFeedRowItem from "%/dynamic-component-editor/standard-elements/StudioFeedRowItem.vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { GlobalFeedElementMixin } from "%/dynamic-component-editor/mixins/global-feed-element-mixin";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinHeight from "%/dynamic-component-editor/options/min-height";
import SmartFeedCardLayout from "%/dynamic-component-editor/options/smart-feed-card-layout";
import SmartFeedLayout from "%/dynamic-component-editor/options/smart-feed-layout";
import { FEED_OPTIONS } from "%/dynamic-component-editor/constants/feed-options";
import { camelToTitleCase, titleToKebabCase } from "@bloglovin/vue-framework";
import SmartFeedPreview from "%/dynamic-component-editor/options/smart-feed-preview";
import { destinationComponentIdByName } from "%/dynamic-component-editor/helpers/destination-component-by-name";
import { resolveNamedContextEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";
import { mapActions, mapState } from "pinia";
import useDynamicComponentStore from "%/dynamic-component-editor/stores/dynamic-component-store";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import HashtagSelector from "%/dynamic-component-editor/options/hashtag-selector";
import StoryTypeSelector from "%/dynamic-component-editor/options/story-type-selector";
import CollectionItemContext from "%/dynamic-component-editor/options/collection-item-context";
import ProductLinkCollection from "%/dynamic-component-editor/options/product-link-collection";
import SmartFeedPagination from "%/dynamic-component-editor/options/smart-feed-pagination";
import SmartFeedPageLimit from "%/dynamic-component-editor/options/smart-feed-page-limit";
import SmartFeedSeeAllDestination from "%/dynamic-component-editor/options/smart-feed-see-all-destination";
import SmartFeedSeeMoreText from "%/dynamic-component-editor/options/smart-feed-see-more-text";
import SmartFeedSeeAllText from "%/dynamic-component-editor/options/smart-feed-see-all-text";
import SmartFeedShowSelection from "%/dynamic-component-editor/options/smart-feed-show-selection";
import SmartFeedSeasonSelection from "%/dynamic-component-editor/options/smart-feed-season-selection";
import LayoutGap from "%/dynamic-component-editor/options/layout-gap";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import GlobalFeedOptions from "%/dynamic-component-editor/bundles/global-feed-options";
import FeedEmptyStateOptions from "%/dynamic-component-editor/bundles/feed-empty-state-options";
import FeedRowComponentId from "%/dynamic-component-editor/options/feed-row-component-id";
import FeedRowContextName from "%/dynamic-component-editor/options/feed-row-context-name";
import SmartFeedSeeMoreAlignment from "%/dynamic-component-editor/options/smart-feed-see-more-alignment";
import VerticalLayoutAlign from "%/dynamic-component-editor/options/vertical-layout-align";
import FontColor from "%/dynamic-component-editor/options/font-color";
import FilterIcon from "%/dynamic-component-editor/options/filter-icon";
import FilterLabelText from "%/dynamic-component-editor/options/filter-label-text";
import FilterToolbarBackgroundColor from "%/dynamic-component-editor/options/filter-toolbar-background-color";
import FilterToolbarFontColor from "%/dynamic-component-editor/options/filter-toolbar-font-color";
import FilterToolbarUnselectedFontColor from "%/dynamic-component-editor/options/filter-toolbar-unselected-font-color";
import FilterToolbarPadding from "%/dynamic-component-editor/options/filter-toolbar-padding";
import FilterToolbarMargins from "%/dynamic-component-editor/options/filter-toolbar-margins";
import FilterToolbarAlignment from "%/dynamic-component-editor/options/filter-toolbar-alignment";
import FilterToolbarLabelPosition from "%/dynamic-component-editor/options/filter-toolbar-label-position";
import FilterToolbarIconColor from "%/dynamic-component-editor/options/filter-toolbar-icon-color";
import {
  SmartFeedButtonElementMixin,
} from "%/dynamic-component-editor/standard-elements/button-element/smart-feed-button-mixin";
import { useCollectionCategoryStore } from "SmartFeed/stores/collection-category-store";
import { usePublicationHashtagStore } from "SmartFeed/stores/publication-hashtag-store";

// "All" pill sentinel — 0 clears the collection_id filter (SmartFeed source
// treats collection_id == 0 as "no specific collection").
const COLLECTION_FILTER_ALL_VALUE = 0;
const COLLECTION_FILTER_KEY = "collection_id";
const COLLECTION_PAGE_CONTEXT_NAME = "collectionItems";

// Mirrors COLLECTION_FILTER_ALL_VALUE: 0 means "no hashtag scoping" on the
// RecentUserStories source, which falls back to the unfiltered recent route.
const HASHTAG_FILTER_ALL_VALUE = 0;
const HASHTAG_FILTER_KEY = "hashtag_id";

// editMode placeholder set so the chip row is visible in the builder preview
// without firing an API call.
const PLACEHOLDER_CATEGORY_COLLECTIONS = [
  { collectionId: -1, name: "Category 1" },
  { collectionId: -2, name: "Category 2" },
  { collectionId: -3, name: "Category 3" },
];

const PLACEHOLDER_PUBLICATION_HASHTAGS = [
  { hashtagId: -1, hashtag: "hashtag-1" },
  { hashtagId: -2, hashtag: "hashtag-2" },
  { hashtagId: -3, hashtag: "hashtag-3" },
];

export default {
  name: "Feed",
  components: {
    SmartFeed,
    StudioFeedRowItem,
  },
  mixins: [
    DynamicElementMixin,
    GlobalFeedElementMixin,
    SmartFeedButtonElementMixin,
  ],
  inject: {
    parentLayoutAlign: { default: null },
    pageEntities: { default: null },
    scopedProvidedContexts: { default: null },
  },
  elementPanelSettings: {
    tags: ["feed", "list", "grid", "collection"],
    displayName: "Feed",
    displayIcon: "feed",
    elementGroup: "general",
    elementType: "element",
  },
  optionBundles: [
    new AppearanceOptions({}, {}, false),
    GlobalFeedOptions,
    new FeedEmptyStateOptions(),
  ],
  options: [
    LayoutGap,
    MaxWidth,
    MinWidth,
    new LayoutWidth({
      "default": "fill",
    }),
  ],
  data() {
    return {
      piniaStore: null,
      MinWidth,
      MinHeight,
      MaxWidth,
      MaxHeight,
      LayoutHeight,
      LayoutWidth,
      SmartFeedSeeAllDestination,
      SmartFeedSeeMoreText,
      SmartFeedSeeMoreAlignment,
      SmartFeedSeeAllText,
      LayoutGap,
      FontColor,
      isFetchingPreviewData: false,
    };
  },
  computed: {
    ...mapState(useFanPublicationStore, { fanPublicationId: "publicationId", orgId: "orgId" }),
    // Resolve the user-facing LayoutWidth option but coerce the legacy
    // "hug" default into "fill" — a freshly-dropped Feed with no width
    // override should expand to fill its parent so the inner SmartFeed
    // grid has room to lay out cards. Users can still pick "hug" or a
    // fixed width explicitly via the inspector.
    resolvedLayoutWidth() {
      const value = this.getOptionValue(LayoutWidth);
      if (!value || value === "hug") {
        return "fill";
      }
      return value;
    },
    // FanPublicationStore is FanWeb-only and stays 0 in Studio routes
    // (`/destination/{id}/...`), so the publication_id filter the
    // RecentStoriesFeedSource (and friends) require would be empty and
    // the SmartFeed would return zero rows. Fall back to the destination
    // id the Studio bootstrap seeds on `window.blclient.data` so the
    // exact same Feed element on a Studio page filters by the right
    // publication without per-route configuration.
    publicationId() {
      if (this.fanPublicationId) {
        return this.fanPublicationId;
      }
      return Number(window?.blclient?.data?.themeDestinationId ?? 0) || 0;
    },
    hasNoFeedSelection() {
      return !this.feedSelection;
    },
    injectedParentLayoutAlign() {
      if (typeof this.parentLayoutAlign === "function") {
        return this.parentLayoutAlign();
      }
      return this.parentLayoutAlign;
    },
    inheritedContentAlignment() {
      return this.injectedParentLayoutAlign || VerticalLayoutAlign.defaultValue;
    },
    filterRowAlignment() {
      return this.getOptionValue(FilterToolbarAlignment) || "left";
    },
    filterRowStyleOverrides() {
      // Margins on the filter row container. Only emit margins the user has
      // explicitly picked so the row's natural gap survives the empty case.
      const margins = this.getOptionValue(FilterToolbarMargins) || {};
      const overrides = {};
      for (const [key, suffix] of [["top", "Top"], ["right", "Right"], ["bottom", "Bottom"], ["left", "Left"]]) {
        if (margins[key] && margins[key] !== "0px") {
          overrides[`margin${suffix}`] = margins[key];
        }
      }
      return overrides;
    },
    filterPillStyleOverrides() {
      // iButton styleGuide per chip: selected=primary (bg/text), unselected=
      // tertiary (text), padding via size-prefixed keys (PILL_SIZE "small").
      const overrides = { borderRadius: "999px" };
      const bgColor = this.getOptionValue(FilterToolbarBackgroundColor);
      if (this.isVisibleColor(bgColor)) {
        overrides.primaryVariantBackgroundColor = bgColor;
        overrides.primaryVariantHoverBackgroundColor = bgColor;
      }
      const fontColor = this.getOptionValue(FilterToolbarFontColor);
      if (fontColor) {
        overrides.primaryVariantFontColor = fontColor;
        overrides.primaryVariantHoverFontColor = fontColor;
      }
      const unselectedFontColor = this.getOptionValue(FilterToolbarUnselectedFontColor);
      if (unselectedFontColor) {
        overrides.tertiaryVariantFontColor = unselectedFontColor;
        overrides.tertiaryVariantHoverFontColor = unselectedFontColor;
      }
      const padding = this.getOptionValue(FilterToolbarPadding) || {};
      for (const [key, suffix] of [["top", "Top"], ["right", "Right"], ["bottom", "Bottom"], ["left", "Left"]]) {
        if (padding[key] && padding[key] !== "0px") {
          overrides[`smallPadding${suffix}`] = padding[key];
        }
      }
      return overrides;
    },
    filterLabelStyleOverrides() {
      // The "Filters" label sits with the unselected pills, so tie it to the
      // unselected font color (selected color stays scoped to the active pill).
      const fontColor = this.getOptionValue(FilterToolbarUnselectedFontColor);
      if (!fontColor) {
        return {};
      }
      return {
        standardFontColor: fontColor,
        standardHoverFontColor: fontColor,
      };
    },
    filterIconStyleOverrides() {
      // iIcon keys color off primaryVariantColor; fall back to a literal so the
      // icon stays visible before the owner picks a color (validator rejects unset).
      const iconColor = this.getOptionValue(FilterToolbarIconColor);
      return { primaryVariantColor: iconColor || "#000000" };
    },
    filterIcon() {
      return this.getOptionValue(FilterIcon);
    },
    filterLabel() {
      return this.getOptionValue(FilterLabelText);
    },
    filterLabelPosition() {
      return this.getOptionValue(FilterToolbarLabelPosition) || "inline";
    },
    publicationOwnerOrgId() {
      // Collection-category fetch is scoped to the publication owner; fall back
      // to this.orgId for call sites that pass the viewer org.
      return useFanPublicationStore(this.$pinia).publicationData?.org_id || this.orgId;
    },
    // TODO: per-feed filter-key wiring belongs in FEED_OPTIONS, not here, so
    // the dynamic element stays generic across feed types.
    isCollectionFilterExposed() {
      return this.filtersToExpose.some(filter => filter.key === COLLECTION_FILTER_KEY);
    },
    isHashtagFilterExposed() {
      return this.filtersToExpose.some(
        filter => filter.key === HASHTAG_FILTER_KEY || filter.key === "tags",
      );
    },
    categoryCollections() {
      // editMode uses static placeholders (no API); production fetches the
      // publication's collections via useCollectionCategoryStore.
      if (this.editMode) {
        return PLACEHOLDER_CATEGORY_COLLECTIONS;
      }
      return useCollectionCategoryStore(this.$pinia).categoryCollections;
    },
    publicationHashtags() {
      if (this.editMode) {
        return PLACEHOLDER_PUBLICATION_HASHTAGS;
      }
      return usePublicationHashtagStore(this.$pinia).publicationHashtags;
    },
    storeFiltersSnapshot() {
      // Reactive read so the watcher re-runs option injection whenever a BE
      // reload reassigns store.filters, keeping the pills populated.
      return this.piniaStore?.()?.filters;
    },
    feedConfig() {
      if (this.hasNoFeedSelection) {
        return {};
      }
      return FEED_OPTIONS[this.feedSelection] || {};
    },
    smartFeedEntityLayout() {
      const variant = this.getOptionValue(SmartFeedCardLayout);
      const activeVariants = Object.keys(this.feedConfig.variants || {});

      if (activeVariants.includes(variant)) {
        return variant;
      }

      return activeVariants[0];
    },
    smartFeedLayout() {
      const layout = this.getOptionValue(SmartFeedLayout);
      const activeLayouts = this.feedConfig.layouts;

      if (!activeLayouts) {
        return null;
      }

      if (activeLayouts.includes(layout)) {
        return layout;
      }

      return activeLayouts[0];
    },
    // #21: list stacks full-width rows, so 3 preview rows read as a list without
    // over-filling the canvas; grid/carousel keep 6 to show the multi-column shape.
    previewCardCount() {
      return this.smartFeedLayout === "list" ? 3 : 6;
    },
    seeAllHref() {
      return this.links.web + "/" + this.getOptionValue(SmartFeedSeeAllDestination);
    },
    showSeeAllOption() {
      return this.smartFeedPaginationOption === SmartFeedPagination.optionValues.linkToViewAllPage.id;
    },
    smartFeedLoadMoreBehavior() {
      const paginationOption = this.getOptionValue(SmartFeedPagination);
      const loadMoreBehaviour = SmartFeedPagination.getOptionMetaData(paginationOption)?.loadMoreBehavior;
      return loadMoreBehaviour || "manual";
    },
    smartFeedPaginationOption() {
      return this.getOptionValue(SmartFeedPagination);
    },
    smartFeedPageSizeLimit() {
      return this.getOptionValue(SmartFeedPageLimit);
    },
    feedSelection() {
      return this.getOptionValue(this.SmartFeedSelection);
    },
    isMissingStoryTypeCard() {
      if (this.feedSelection !== "StoriesByType") {
        return false;
      }
      const rowDcId = Number(this.getOptionValue(FeedRowComponentId));
      if (Number.isFinite(rowDcId) && rowDcId > 0) {
        return false;
      }
      return this.resolveStoryTypeCardId() === 0;
    },
    feedSource() {
      return this.feedConfig.source;
    },
    shouldShowPlaceholders() {
      return this.editMode && !this.getOptionValue(SmartFeedPreview);
    },
    smartFeedKey() {
      return `${this.feedSelection}-${this.smartFeedLayout}-${this.smartFeedEntityLayout}`;
    },
    collectionItemContext() {
      return this.getOptionValue(CollectionItemContext);
    },
    productCollection() {
      return this.getOptionValue(ProductLinkCollection);
    },
    // No collection picked: on a Collection Items page that is the page's own collection; anywhere else
    // the feed lists every public collection of the destination.
    productLinkCollectionId() {
      return this.productCollection || resolveNamedContextEntityId(this, COLLECTION_PAGE_CONTEXT_NAME) || "";
    },
    showSelection() {
      return this.getOptionValue(SmartFeedShowSelection);
    },
    seasonSelection() {
      return this.getOptionValue(SmartFeedSeasonSelection);
    },
    filtersToExpose() {
      const value = this.getOptionValue(this.SmartFeedFiltersToShow);
      return Array.isArray(value) ? value : [];
    },
    rowTemplateComponentId() {
      const raw = this.getOptionValue(FeedRowComponentId);
      const asNumber = Number(raw);
      if (Number.isFinite(asNumber) && asNumber > 0) {
        return asNumber;
      }
      const storyTypeCardId = this.resolveStoryTypeCardId();
      if (storyTypeCardId > 0) {
        return storyTypeCardId;
      }
      // Studio-only fallback: when the author hasn't explicitly picked a
      // row template DC, look up the destination's seeded "Story Card" /
      // "Large Story Card" / etc. component by name on the App Engine
      // DynamicComponent entity store. The seeder names them in title
      // case ("StoryCard" → "Story Card"), matching the camelToTitleCase
      // transform used by `Feed.vue`'s preloadDynamicComponents and by
      // `InspectorHeaderV2.resolveDcIdByClassName` for the Edit Component
      // menu. Outside Studio (FanWeb / Develop / legacy /build/<dest>)
      // the App Engine store isn't registered and this returns 0,
      // leaving SmartFeedItem's legacy engine lookup intact.
      const variants = this.feedConfig?.variants;
      const relatedClass = variants?.[this.smartFeedEntityLayout];
      if (!relatedClass) {
        return 0;
      }
      return this.resolveDestinationComponentIdByName(camelToTitleCase(relatedClass, true));
    },
    // Pageentities key the row template's elements read from. Authors can
    // override per-element via FeedRowContextName; otherwise we fall back
    // to the `rowContextName` declared on the picked FEED_OPTIONS entry
    // so the common case (HomeStories + StoryCard) needs zero typing.
    rowTemplateContextName() {
      const explicit = this.getOptionValue(FeedRowContextName);
      if (explicit) {
        return explicit;
      }
      return this.feedConfig?.rowContextName ?? "";
    },
  },
  watch: {
    isCollectionFilterExposed: {
      immediate: true,
      handler(value) {
        if (!value || this.editMode) {
          return;
        }
        useCollectionCategoryStore(this.$pinia)
            .fetchCategoryCollectionsForPublication(this.publicationOwnerOrgId, this.publicationId);
      },
    },
    isHashtagFilterExposed: {
      immediate: true,
      handler(value) {
        if (!value || this.editMode) {
          return;
        }
        usePublicationHashtagStore(this.$pinia)
            .fetchPublicationHashtagsForPublication(this.publicationId);
      },
    },
    // Seed exposed filters' ui_type + options as fetched data / reloads land.
    categoryCollections: {
      immediate: true,
      handler() {
        this.injectExposedFilterOptions();
      },
    },
    publicationHashtags: {
      immediate: true,
      handler() {
        this.injectExposedFilterOptions();
      },
    },
    storeFiltersSnapshot: {
      immediate: true,
      handler() {
        this.injectExposedFilterOptions();
      },
    },
    // Re-seed when the author toggles a filter / changes its ui type, so it
    // shows without a reload.
    filtersToExpose: {
      immediate: true,
      deep: true,
      handler() {
        this.injectExposedFilterOptions();
      },
    },
    productCollection() {
      if (this.editMode) {
        this.loadPreviewData();
      }
    },
    showSelection(newValue, oldValue) {
      if (!this.editMode) {
        return;
      }

      if (newValue !== oldValue && this.seasonSelection) {
        this.configuration["default.smart-feed-season-selection"] = "";
      }

      if (["ShowSeasonsFeedSource", "ShowSeasonEpisodesFeedSource"].includes(this.feedSelection)) {
        this.loadPreviewData();
      }
    },
    seasonSelection() {
      if (this.editMode && this.feedSelection === "ShowSeasonEpisodesFeedSource") {
        this.loadPreviewData();
      }
    },
    collectionItemContext: {
      immediate: true,
      handler(value) {
        if (this.feedSelection === "Collections") {
          this.feedSource().metadata.itemContext = value;
        }

        if (this.editMode) {
          this.loadPreviewData();
        }
      },
    },
    feedSelection: {
      immediate: true,
      handler(value) {
        if (value) {
          if (!FEED_OPTIONS[this.feedSelection]) {
            throw new DynamicComponentError(
              "Invalid smartfeed option key",
              {
                selection: value,
              },
            );
          }
        }
      },
    },
    smartFeedLayout() {
      const layout = this.getOptionValue(SmartFeedLayout);
      const activeLayouts = this.feedConfig.layouts;

      if (!activeLayouts.includes(layout)) {
        if (this.editMode) {
          this.configuration["default.smart-feed-layout"] = activeLayouts[0];
        }
      }
    },
    smartFeedEntityLayout: {
      immediate: true,
      handler() {
        const variant = this.getOptionValue(SmartFeedCardLayout);

        const activeVariants = Object.keys(this.feedConfig.variants || {});

        if (!activeVariants.includes(variant)) {
          if (this.editMode) {
            this.configuration["default.smart-feed-card-layout"] = activeVariants[0];
          }
        }

        if (this.editMode) {
          this.loadPreviewData();
        }
      },
    },
    // Re-render the preview rows whenever the author picks (or
    // changes) the row-template DC in the inspector — without this,
    // selecting the row DC after the feed source already loaded
    // leaves the canvas on whatever the previous path rendered.
    rowTemplateComponentId() {
      if (this.editMode && this.piniaStore) {
        this.loadPreviewData();
      }
    },
    feedSource: {
      immediate: true,
      handler(newVal) {
        this.piniaStore = newVal;
        // Seed the smart-feed store's filters synchronously here, BEFORE
        // SmartFeedV2's `created` hook fires `getSmartFeed()`. The
        // editor-mode `loadPreviewData` path also seeds them later, but
        // it does so after an async dynamic `import()` — leaving a
        // window where SmartFeedV2 dispatches a request with `filters={}`
        // and the backend RecentStoriesFeedSource (and friends) default
        // `publication_id` to 0, blowing up `Publication::getBasicData(0)`.
        if (this.piniaStore && !this.hasNoFeedSelection) {
          const store = this.piniaStore();
          if (store) {
            store.filters = this.getDefaultFiltersForFeed();
          }
        }
        if (this.editMode && this.piniaStore) {
          this.seedPlaceholderRows();
          this.loadPreviewData();
        }
      },
    },
  },
  methods: {
    ...mapActions(useDynamicComponentStore, ["getOrFetchDynamicComponentConfigurationLayout"]),
    // Builder preview only; live renders (editMode=false) click through.
    handleEditorPreviewClick(event) {
      if (!this.editMode) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    isVisibleColor(value) {
      // Treat unset / transparent values as "not user-set" so the variant's
      // default background shows through instead of a forced override.
      if (!value || value === "none" || value === "transparent" || value === "unset") {
        return false;
      }
      const normalized = String(value).replace(/\s+/g, "").toLowerCase();
      if (normalized.startsWith("rgba(") && normalized.endsWith(",0)")) {
        return false;
      }
      return true;
    },
    // { options, allValue } for one exposed filter key. editMode uses static
    // placeholders; production reads the publication data stores.
    filterOptionSetForKey(key) {
      if (key === COLLECTION_FILTER_KEY) {
        const collections = this.categoryCollections || [];
        return {
          allValue: COLLECTION_FILTER_ALL_VALUE,
          options: [
            { label: "All", value: COLLECTION_FILTER_ALL_VALUE },
            ...collections.map(collection => ({ label: collection.name, value: collection.collectionId })),
          ],
        };
      }
      // Story sources match on hashtag id; the Videos "tags" source on the slug.
      if (key === HASHTAG_FILTER_KEY || key === "tags") {
        const hashtags = this.publicationHashtags || [];
        const usesSlug = key === "tags";
        const allValue = usesSlug ? "" : HASHTAG_FILTER_ALL_VALUE;
        return {
          allValue,
          options: [
            { label: "All", value: allValue },
            ...hashtags.map(hashtag => ({
              label: `#${hashtag.hashtag}`,
              value: usesSlug ? hashtag.hashtag : hashtag.hashtagId,
            })),
          ],
        };
      }
      // Unknown key: placeholder chips in the builder; backend options in prod.
      if (this.editMode) {
        return {
          allValue: "",
          options: [
            { label: "All", value: "" },
            { label: "Option 1", value: "option-1" },
            { label: "Option 2", value: "option-2" },
            { label: "Option 3", value: "option-3" },
          ],
        };
      }
      return { allValue: "", options: [] };
    },
    // Ensure every exposed filter has a store entry with ui_type + options.
    injectExposedFilterOptions() {
      const store = this.piniaStore?.();
      if (!store || !this.filtersToExpose.length) {
        return;
      }
      const available = (this.feedConfig || {}).availableFilters || [];
      const defaultUiByKey = {};
      const labelByKey = {};
      available.forEach(a => {
        defaultUiByKey[a.key] = a.defaultUiType || (a.compatibleUiTypes || [])[0];
        labelByKey[a.key] = a.label;
      });
      const { filters, needsReassign } = buildExposedFilterStoreEntries(
        store.filters,
        this.filtersToExpose,
        {
          optionSetForKey: key => this.filterOptionSetForKey(key),
          defaultUiByKey,
          labelByKey,
        },
      );
      if (needsReassign) {
        store.filters = filters;
      }
    },
    setFeedEntities(feed, entities) {
      // The `smartFeedEntityLayout` watcher fires `immediate: true`,
      // which lands BEFORE the `feedSource` watcher has assigned
      // `this.piniaStore`. Bail if the store isn't ready yet — the
      // feedSource watcher's own loadPreviewData call will re-enter
      // this method once the store lands. Pre-fix this threw
      // "this.piniaStore is not a function" on every Feed drop and
      // the inspector never finished mounting.
      if (typeof this.piniaStore !== "function") {
        return;
      }
      this.piniaStore().$reset();
      this.piniaStore().handleSmartFeedResponse({
        entities,
        feed,
        metaData: {
          hasMore: false,
        },
      });
    },
    async preloadDynamicComponents(className) {
      const dynamicComponents = Object.values(useDynamicComponentStore().dynamicComponents);
      const matchedComponents = dynamicComponents.filter(component => component.class === className);

      if (!matchedComponents.length) {
        await this.getOrFetchDynamicComponentConfigurationLayout(camelToTitleCase(className, true), false);
      }

      return true;
    },
    getSelectionId(selection) {
      if (selection && typeof selection === "object") {
        return selection.value;
      }

      return selection;
    },
    getDefaultFiltersForFeed() {
      const feedSelection = this.getOptionValue(this.SmartFeedSelection);
      const filters = [
        {
          "key": "publication_id",
          "value": this.publicationId,
        },
      ];

      if (feedSelection === "StoriesWithHashtag") {
        filters.push({
          "key": "hashtag",
          "value": this.getOptionValue(HashtagSelector),
        });
      }

      if (["RecentUserStories", "RecentStories", "HomeStories", "StoriesByType"].includes(feedSelection)) {
        // Seed hashtag_id at the All sentinel so the BE source falls back to
        // the unfiltered recent route until a chip is clicked.
        filters.push({
          "key": HASHTAG_FILTER_KEY,
          "value": HASHTAG_FILTER_ALL_VALUE,
        });
      }

      if (feedSelection === "StoriesByType") {
        filters.push({
          "key": "story_type",
          "value": this.getOptionValue(StoryTypeSelector),
        });
      }

      if (feedSelection === "Videos") {
        filters.push({
          "key": "variant_type",
          "value": "video",
        });
      }

      if (feedSelection === "ProductLinkCollection") {
        filters.push({
          "key": "collection_id",
          "value": this.productLinkCollectionId,
        });
      }

      if (feedSelection === "Collections") {
        // The destination's collections, not the viewer's: a visitor's own org is 0 or someone else's.
        filters.push({
          "key": "org_id",
          "value": this.publicationOwnerOrgId,
        });
        filters.push({
          "key": "publication_id",
          "value": this.publicationId,
        });
      }

      const showId = this.getSelectionId(this.showSelection);
      const seasonId = this.getSelectionId(this.seasonSelection);

      if (feedSelection === "ShowSeasonsFeedSource" && showId) {
        filters.push({
          "key": "show_id",
          "value": showId,
        });
      }

      if (feedSelection === "ShowSeasonEpisodesFeedSource") {
        if (showId) {
          filters.push({
            "key": "show_id",
            "value": showId,
          });
        }
        if (seasonId) {
          filters.push({
            "key": "season_id",
            "value": seasonId,
          });
        }
      }
      return filters;
    },
    resolveStoryTypeCardId() {
      if (this.feedSelection !== "StoriesByType") {
        return 0;
      }
      const storyType = this.getOptionValue(StoryTypeSelector);
      if (!storyType) {
        return 0;
      }
      const targetName = storyType.charAt(0).toUpperCase() + storyType.slice(1) + " Card";
      return this.resolveDestinationComponentIdByName(targetName);
    },
    resolveDestinationComponentIdByName(targetName) {
      return destinationComponentIdByName(targetName, this.publicationId);
    },
    seedPlaceholderRows() {
      const stubType = (this.feedConfig?.entities ?? [])[0] ?? this.rowTemplateContextName ?? "preview";
      const stubFeed = new Array(this.previewCardCount).fill("").map((_, index) => ({
        id: index + 1,
        type: stubType,
      }));
      const stubEntities = {
        [stubType]: stubFeed.reduce((map, item) => {
          map[item.id] = { id: item.id };
          return map;
        }, {}),
      };
      this.setFeedEntities(stubFeed, stubEntities);
    },
    async loadPreviewData() {
      if (this.isFetchingPreviewData || this.hasNoFeedSelection) {
        return;
      }
      this.isFetchingPreviewData = true;

      this.seedPlaceholderRows();

      // Custom-DC-row branch: when the author picked a destination's
      // own DynamicComponent (StoryCard, EventCard, …) as the row
      // template, we stuff N placeholder feed entries straight into
      // the SmartFeed store and let each row mount the picked DC. The
      // DC's element mixins inject `useExampleData` (provided by
      // StudioFeedRowItem in the `#item` slot when `editMode` is on) and
      // fall back to their `getExampleEntity()` payloads — so the
      // canvas preview shows what the published feed will look like
      // without any network fetch. We populate `entities` with one
      // stub per row so handleSmartFeedResponse's entity-type validator
      // doesn't bail on an empty payload — the actual rendered values
      // come from the per-mixin getExampleEntity, not from these stubs.
      if (this.rowTemplateComponentId > 0) {
        try {
          const relatedEntityType = (this.feedConfig?.entities ?? [])[0] ?? this.rowTemplateContextName ?? "preview";
          const placeholderFeed = new Array(this.previewCardCount).fill("").map((_, index) => ({
            id: index + 1,
            type: relatedEntityType,
          }));
          const placeholderEntities = {
            [relatedEntityType]: placeholderFeed.reduce((map, item) => {
              map[item.id] = { id: item.id };
              return map;
            }, {}),
          };
          this.setFeedEntities(placeholderFeed, placeholderEntities);
        } finally {
          this.isFetchingPreviewData = false;
        }
        return;
      }

      const variants = this.feedConfig.variants;

      const componentClassName = variants[this.smartFeedEntityLayout];
      if (!componentClassName) {
        // Feed config has no card-class mapping for the current
        // smartFeedEntityLayout (typical for Studio's myFeed source
        // before a row template DC is picked). Bail before the dynamic
        // mixin import / preload runs against an `undefined` class —
        // preloadDynamicComponents → configuration-store would otherwise
        // call Object.values on an unhydrated dynamicComponents map and
        // throw "Cannot convert undefined or null to object".
        this.isFetchingPreviewData = false;
        return;
      }
      const componentFolderName = titleToKebabCase(componentClassName);
      const mixinFileName = `${componentFolderName}-element-mixin.js`;
      const mixinName = `${componentClassName}ElementMixin`;

      const relatedEntity = this.feedConfig.entities[0];

      const mixin = await import(`~/dynamic-components/standard-components/${componentFolderName}/${mixinFileName}`);
      await this.preloadDynamicComponents(componentClassName);

      const entityPreviewData = await mixin[mixinName].methods.getExampleEntity();
      const entities = new Array(this.previewCardCount).fill("").reduce((entityMap, _, index) => {
        return {
          ...entityMap,
          [relatedEntity]: {
            ...entityMap[relatedEntity],
            [index + 1]: entityPreviewData,
          },
        };
      }, {});
      const feed = new Array(this.previewCardCount).fill("").map((_, index) => {
        return {
          id: index + 1,
          type: relatedEntity,
        };
      });
      this.setFeedEntities(feed, entities);
      this.isFetchingPreviewData = false;
    },
  },
};
</script>

<style lang="scss" scoped>
// No layout box; stays in the event path without shifting SmartFeed.
.feed-editor-click-guard {
  display: contents;
}
</style>
